import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calculator } from "lucide-react";
import { NestPointsDisplay } from "./NestPointsDisplay";
import { useTranslation } from "react-i18next";

interface WorkationCalculatorProps {
  policy: {
    monthly_points_credit: number;
    maximum_balance: number;
    base_points_per_week: number;
  };
  distancePricing: Array<{
    timezone_difference_hours: number;
    points_per_week_multiplier: number;
    distance_category: string;
  }>;
}

export const WorkationCalculator = ({ policy, distancePricing }: WorkationCalculatorProps) => {
  const { t } = useTranslation();
  const monthlyPoints = policy.monthly_points_credit;
  const maxBalance = policy.maximum_balance;
  const basePoints = policy.base_points_per_week;

  // Calculate weeks possible with current balance
  const weeksWithMonthly = Math.floor(monthlyPoints / basePoints);
  const weeksWithMax = Math.floor(maxBalance / basePoints);

  // Get pricing for different timezone differences
  const sameTimezone = distancePricing.find(p => p.timezone_difference_hours === 0) || { points_per_week_multiplier: 1.0 };
  const plus2Hours = distancePricing.find(p => p.timezone_difference_hours === 2) || { points_per_week_multiplier: 1.5 };
  const plus4Hours = distancePricing.find(p => p.timezone_difference_hours === 4) || { points_per_week_multiplier: 2.0 };

  const calculatePoints = (weeks: number, multiplier: number) => {
    return Math.round(basePoints * multiplier * weeks);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5" />
            {t("dashboard.governance.calculator.title", "Workation Calculator")}
          </CardTitle>
          <CardDescription>
            {t("dashboard.governance.calculator.subtitle", "Visualize the impact of your policy on workation capacity")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {/* With Monthly Credit */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{t("dashboard.governance.calculator.withMonthlyPoints", "With monthly points")} ({monthlyPoints} pts)</span>
              </div>
              <div className="text-3xl font-bold">{weeksWithMonthly} {t("dashboard.governance.costExamples.weeks", "weeks")}</div>
              <p className="text-sm text-muted-foreground">
                {t("dashboard.governance.calculator.weeksPerMonth", "weeks per month possible (same timezone)")}
              </p>
            </div>

            {/* With Maximum Balance */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{t("dashboard.governance.calculator.withMaxBalance", "With max balance")}</span>
              </div>
              <div className="text-3xl font-bold">{weeksWithMax} {t("dashboard.governance.costExamples.weeks", "weeks")}</div>
              <p className="text-sm text-muted-foreground">
                {t("dashboard.governance.calculator.maxAccumulable", "maximum accumulable")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cost Examples Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("dashboard.governance.costExamples.title", "Cost Examples")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium">{t("dashboard.governance.costExamples.duration", "Duration")}</th>
                  <th className="text-left py-3 px-4 font-medium">{t("dashboard.governance.costExamples.sameTimezone", "Same timezone")}</th>
                  <th className="text-left py-3 px-4 font-medium">±2h</th>
                  <th className="text-left py-3 px-4 font-medium">±4h</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3 px-4">1 {t("dashboard.governance.costExamples.week", "week")}</td>
                  <td className="py-3 px-4">
                    <NestPointsDisplay 
                      points={calculatePoints(1, sameTimezone.points_per_week_multiplier)}
                      showIcon={false}
                      size="sm"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <NestPointsDisplay 
                      points={calculatePoints(1, plus2Hours.points_per_week_multiplier)}
                      showIcon={false}
                      size="sm"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <NestPointsDisplay 
                      points={calculatePoints(1, plus4Hours.points_per_week_multiplier)}
                      showIcon={false}
                      size="sm"
                    />
                  </td>
                </tr>
                <tr className="border-b">
                  <td className="py-3 px-4">2 {t("dashboard.governance.costExamples.weeks", "weeks")}</td>
                  <td className="py-3 px-4">
                    <NestPointsDisplay 
                      points={calculatePoints(2, sameTimezone.points_per_week_multiplier)}
                      showIcon={false}
                      size="sm"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <NestPointsDisplay 
                      points={calculatePoints(2, plus2Hours.points_per_week_multiplier)}
                      showIcon={false}
                      size="sm"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <NestPointsDisplay 
                      points={calculatePoints(2, plus4Hours.points_per_week_multiplier)}
                      showIcon={false}
                      size="sm"
                    />
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4">1 {t("dashboard.governance.costExamples.month", "month (4 weeks)")}</td>
                  <td className="py-3 px-4">
                    <NestPointsDisplay 
                      points={calculatePoints(4, sameTimezone.points_per_week_multiplier)}
                      showIcon={false}
                      size="sm"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <NestPointsDisplay 
                      points={calculatePoints(4, plus2Hours.points_per_week_multiplier)}
                      showIcon={false}
                      size="sm"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <NestPointsDisplay 
                      points={calculatePoints(4, plus4Hours.points_per_week_multiplier)}
                      showIcon={false}
                      size="sm"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};